import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Typography } from "@/components/ui/typography"
import { useEmailSenderIdentity } from "@/features/material-compliance/api/inquiryEmailApi"
import type {
  EmailCopy,
  InquiryTemplateSettings,
} from "@/features/material-compliance/components/supplier-declarations/inquiry/settings/templateSettingsMock"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { TriangleAlert } from "lucide-react"
import { useState } from "react"
import { Link } from "react-router"
import {
  LanguageSwitch,
  type ContentLanguage,
} from "../../../components/SettingsControls"
import {
  SettingsField,
  SettingsSection,
} from "../../../components/SettingsPage"
import { SettingsWithPreview } from "../../../components/SettingsPreview"
import { TemplateSaveButton } from "../../../components/TemplateSaveButton"
import { TemplateSettingsLoader } from "../../../components/TemplateSettingsLoader"
import { sameContent, useSavedDraft } from "../../../hooks/useSavedDraft"
import {
  useTemplateSettingsSave,
  type TemplateSettingsSaver,
} from "../../../hooks/useTemplateSettingsSave"
import { SETTINGS_PATHS } from "../../../paths"
import {
  EmailPreview,
  resolvePreviewVariables,
} from "../../../previews/EmailPreview"
import { needsWriting, placeholderTokens } from "../../../setup/placeholders"
import {
  containsReferenceToken,
  invalidAddresses,
  namesDeadline,
  namesRegulations,
  unknownVariables,
} from "./templateChecks"
import { TemplateBodyField, TemplateSubjectField } from "./TemplateFields"

type CopiesByLanguage = Record<ContentLanguage, EmailCopy>

const sameCopies = (a: CopiesByLanguage, b: CopiesByLanguage) =>
  a.en.subject === b.en.subject &&
  a.en.body === b.en.body &&
  a.de.subject === b.de.subject &&
  a.de.body === b.de.body

/** One comma- or semicolon-separated field; the backend checks each address. */
const parseCc = (text: string) =>
  text
    .split(/[,;]/)
    .map((address) => address.trim())
    .filter(Boolean)

/** What would make the backend refuse these texts, as a sentence, or null. */
function useTemplateProblem(texts: string[]): string | null {
  const { translate } = useTranslation()
  const unknown = unknownVariables(texts)
  if (unknown.length > 0)
    return translate("SETTINGS/SUPPLIER_INQUIRIES/UNKNOWN_VARIABLE", {
      names: unknown.join(", "),
    })
  if (containsReferenceToken(texts))
    return translate("SETTINGS/SUPPLIER_INQUIRIES/REFERENCE_TOKEN")
  return null
}

/** A body that doesn't name the asked regulations yet: said, not refused. The shipped
 *  texts name them; an org's own keeps its wording until it adds the token (D3). */
function RegulationsHint({
  body,
  language,
}: {
  body: string
  language: ContentLanguage
}) {
  const { translate } = useTranslation()
  if (namesRegulations(body)) return null
  return (
    <Typography as="muted">
      {translate("SETTINGS/SUPPLIER_INQUIRIES/NO_REGULATIONS_TOKEN", {
        token: language === "de" ? "{{regelwerke}}" : "{{regulations}}",
      })}
    </Typography>
  )
}

/** Where the emails come from: set on the Email sending page. */
function SenderLine() {
  const { translate } = useTranslation()
  const { data: identity } = useEmailSenderIdentity()
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <Typography as="muted">
        {identity?.fullAddress
          ? translate("SETTINGS/SUPPLIER_INQUIRIES/SENT_FROM", {
              address: identity.fullAddress,
            })
          : translate("SETTINGS/SUPPLIER_INQUIRIES/NO_SENDER")}
      </Typography>
      <Button variant="link" asChild className="h-auto p-0">
        <Link to={SETTINGS_PATHS.emailSending} replace>
          {translate("SETTINGS/SUPPLIER_INQUIRIES/CHANGE_SENDER")}
        </Link>
      </Button>
    </div>
  )
}

/** The signature in the language on screen still needs writing: it goes out
 *  under every email, so it is pointed out here too, with the way to it. */
function SignatureTodo() {
  const { translate } = useTranslation()
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-warning/40 bg-warning/10 px-3 py-2">
      <TriangleAlert aria-hidden className="size-4 shrink-0" />
      <Typography as="small" className="font-normal">
        {translate("SETTINGS/SUPPLIER_INQUIRIES/SIGNATURE_TODO")}
      </Typography>
      <Button variant="link" asChild className="h-auto p-0">
        <Link to={SETTINGS_PATHS.emailSending} replace>
          {translate("SETTINGS/SUPPLIER_INQUIRIES/EDIT_SIGNATURE")}
        </Link>
      </Button>
    </div>
  )
}

/** Who is copied on every new inquiry. Also opened from the inquiry draft,
 *  beside the draft, through `DefaultCcSettings`. */
function DefaultCcSection({
  saved,
  saver,
}: {
  saved: InquiryTemplateSettings
  saver: TemplateSettingsSaver
}) {
  const { translate } = useTranslation()
  const [ccText, setCcText] = useSavedDraft(saved.defaultCc.join(", "))
  const cc = parseCc(ccText)
  const badAddresses = invalidAddresses(cc)
  const readOnly = !saver.canEdit

  return (
    <SettingsSection
      title={translate("MC_TPL_EC_CC_HEADING")}
      target="inquiry.cc"
      info={translate("SETTINGS/INFO/NEW_ONLY")}
      description={translate("MC_TPL_EC_CC_NOTE")}
      footer={
        <TemplateSaveButton
          saver={saver}
          saveKey="default-cc"
          dirty={cc.join(",") !== saved.defaultCc.join(",")}
          invalid={badAddresses.length > 0}
          patch={() => ({ defaultCc: cc })}
        />
      }
    >
      <Input
        aria-label={translate("MC_TPL_EC_CC_HEADING")}
        aria-invalid={badAddresses.length > 0}
        aria-describedby={
          badAddresses.length > 0 ? "settings-cc-problem" : undefined
        }
        value={ccText}
        disabled={readOnly}
        placeholder={translate("MC_TPL_EC_CC_PLACEHOLDER")}
        onChange={(event) => setCcText(event.target.value)}
      />
      {badAddresses.length > 0 && (
        <Typography as="muted" id="settings-cc-problem">
          {translate("SETTINGS/SUPPLIER_INQUIRIES/INVALID_CC", {
            addresses: badAddresses.join(", "),
          })}
        </Typography>
      )}
    </SettingsSection>
  )
}

/** The default Cc on its own, with its own Save: for the inquiry draft's
 *  side sheet. */
export function DefaultCcSettings() {
  const saver = useTemplateSettingsSave()
  return (
    <TemplateSettingsLoader>
      {(settings) => <DefaultCcSection saved={settings} saver={saver} />}
    </TemplateSettingsLoader>
  )
}

function EmailsForm({
  saved,
  saver,
}: {
  saved: InquiryTemplateSettings
  saver: TemplateSettingsSaver
}) {
  const { translate, currentLanguage } = useTranslation()
  const uiLanguage: ContentLanguage = currentLanguage === "DE" ? "de" : "en"
  // A language switch per email, in its own header: the texts are written
  // per language, while the address they come from is one for both.
  const [firstLanguage, setFirstLanguage] = useState(uiLanguage)
  const [reminderLanguage, setReminderLanguage] = useState(uiLanguage)
  const [first, setFirst] = useSavedDraft(
    saved.emailTemplates.first,
    sameContent,
  )
  const [reminder, setReminder] = useSavedDraft(
    saved.emailTemplates.reminder,
    sameContent,
  )
  const readOnly = !saver.canEdit
  const firstSubject = (lang: ContentLanguage) =>
    resolvePreviewVariables(first[lang].subject, lang)
  // Both languages: the one on screen may be fine while the other is not.
  const firstProblem = useTemplateProblem([
    first.en.subject,
    first.en.body,
    first.de.subject,
    first.de.body,
  ])
  const reminderProblem = useTemplateProblem([
    reminder.en.body,
    reminder.de.body,
  ])
  // Per language, what an email in it would still go out with: a
  // placeholder in its own texts, or a signature that needs writing.
  const stillToWrite = (texts: (lang: ContentLanguage) => string[]) => {
    const pending = (lang: ContentLanguage) =>
      placeholderTokens(texts(lang).join("\n")).length > 0 ||
      needsWriting(saved.emailSignatures[lang])
    return { en: pending("en"), de: pending("de") }
  }

  return (
    <div className="flex flex-col gap-6">
      <SenderLine />
      <div className="flex flex-col divide-y">
        <SettingsSection
          title={translate("MC_TPL_EC_FIRST_HEADING")}
          target="inquiry.firstEmail"
          info={translate("SETTINGS/INFO/NEW_ONLY")}
          description={translate("MC_TPL_EC_DESC")}
          action={
            <LanguageSwitch
              value={firstLanguage}
              onChange={setFirstLanguage}
              needsWriting={stillToWrite((lang) => [
                first[lang].subject,
                first[lang].body,
              ])}
            />
          }
          footer={
            <TemplateSaveButton
              saver={saver}
              saveKey="first-email"
              dirty={!sameCopies(first, saved.emailTemplates.first)}
              invalid={firstProblem !== null}
              patch={() => ({ emailTemplates: { first } })}
            />
          }
        >
          {needsWriting(saved.emailSignatures[firstLanguage]) && (
            <SignatureTodo />
          )}
          <SettingsWithPreview
            preview={
              <EmailPreview
                subject={firstSubject(firstLanguage)}
                body={resolvePreviewVariables(
                  first[firstLanguage].body,
                  firstLanguage,
                )}
                footer={saved.emailSignatures[firstLanguage]}
              />
            }
          >
            <SettingsField
              htmlFor="settings-first-subject"
              label={translate("MC_TPL_EC_SUBJECT_LABEL")}
            >
              <TemplateSubjectField
                id="settings-first-subject"
                value={first[firstLanguage].subject}
                language={firstLanguage}
                disabled={readOnly}
                onChange={(subject) =>
                  setFirst({
                    ...first,
                    [firstLanguage]: { ...first[firstLanguage], subject },
                  })
                }
              />
            </SettingsField>
            {namesDeadline(first[firstLanguage].subject) && (
              <Typography as="muted">
                {translate("SETTINGS/SUPPLIER_INQUIRIES/DEADLINE_IN_SUBJECT")}
              </Typography>
            )}
            <SettingsField label={translate("MC_TPL_EC_BODY_LABEL")}>
              <TemplateBodyField
                value={first[firstLanguage].body}
                language={firstLanguage}
                disabled={readOnly}
                onChange={(body) =>
                  setFirst({
                    ...first,
                    [firstLanguage]: { ...first[firstLanguage], body },
                  })
                }
              />
            </SettingsField>
            <RegulationsHint
              body={first[firstLanguage].body}
              language={firstLanguage}
            />
            {firstProblem && (
              <Typography as="muted" role="alert">
                {firstProblem}
              </Typography>
            )}
          </SettingsWithPreview>
        </SettingsSection>

        <SettingsSection
          title={translate("MC_TPL_EC_REMINDER_HEADING")}
          target="inquiry.reminderEmail"
          info={translate("SETTINGS/INFO/ALSO_RUNNING")}
          description={translate("MC_TPL_EC_REMINDER_NOTE")}
          action={
            <LanguageSwitch
              value={reminderLanguage}
              onChange={setReminderLanguage}
              needsWriting={stillToWrite((lang) => [reminder[lang].body])}
            />
          }
          footer={
            <TemplateSaveButton
              saver={saver}
              saveKey="reminder-email"
              dirty={!sameCopies(reminder, saved.emailTemplates.reminder)}
              invalid={reminderProblem !== null}
              // The message only: the subject is always "Re: <original>".
              patch={() => ({
                emailTemplates: {
                  reminder: {
                    en: { body: reminder.en.body },
                    de: { body: reminder.de.body },
                  },
                },
              })}
            />
          }
        >
          {needsWriting(saved.emailSignatures[reminderLanguage]) && (
            <SignatureTodo />
          )}
          <SettingsWithPreview
            preview={
              <EmailPreview
                // Sent as a reply in the first email's thread.
                subject={`Re: ${firstSubject(reminderLanguage)}`}
                body={resolvePreviewVariables(
                  reminder[reminderLanguage].body,
                  reminderLanguage,
                )}
                footer={saved.emailSignatures[reminderLanguage]}
              />
            }
          >
            <TemplateBodyField
              value={reminder[reminderLanguage].body}
              language={reminderLanguage}
              disabled={readOnly}
              onChange={(body) =>
                setReminder({
                  ...reminder,
                  [reminderLanguage]: { ...reminder[reminderLanguage], body },
                })
              }
            />
            <RegulationsHint
              body={reminder[reminderLanguage].body}
              language={reminderLanguage}
            />
            {reminderProblem && (
              <Typography as="muted" role="alert">
                {reminderProblem}
              </Typography>
            )}
          </SettingsWithPreview>
        </SettingsSection>

        <DefaultCcSection saved={saved} saver={saver} />
      </div>
    </div>
  )
}

/** The emails a supplier inquiry sends: the first request, the reminder,
 *  and who is copied on new inquiries. */
export function EmailsTab({ saver }: { saver: TemplateSettingsSaver }) {
  return (
    <TemplateSettingsLoader>
      {(settings) => <EmailsForm saved={settings} saver={saver} />}
    </TemplateSettingsLoader>
  )
}
